import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Device } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useTableColumns } from '../../hooks/use-table-columns'
import { InstrumentConfigEditModal } from './InstrumentConfigEditModal'

export function DeviceListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage } = useApiList<Device>({
    path: '/devices',
    filters: { search: debouncedSearch || undefined },
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('devices')
  const [configDeviceId, setConfigDeviceId] = useState<string | null>(null)
  const [configDeviceName, setConfigDeviceName] = useState('')

  const columns: readonly Column<Device>[] = [
    { key: 'name', header: 'Name', sortable: true, filterable: true, render: (d) => <strong>{d.name}</strong> },
    { key: 'serial', header: 'Serial Number', sortable: true, filterable: true, render: (d) => d.serialNumber },
    { key: 'type', header: 'Type', sortable: true, filterable: true, render: (d) => d.deviceType },
    { key: 'status', header: 'Status', sortable: true, render: (d) => <StatusBadge status={d.status} /> },
    {
      key: 'calibration',
      header: 'Next Calibration',
      sortable: true,
      render: (d) => (d.nextCalibrationDue ? new Date(d.nextCalibrationDue).toLocaleDateString() : '--'),
      sortValue: (d) => (d.nextCalibrationDue ? new Date(d.nextCalibrationDue).getTime() : 0),
    },
    {
      key: 'config',
      header: 'Config',
      render: (d) => (
        <button
          style={{ ...btnSecondary, fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
          onClick={(e) => {
            e.stopPropagation()
            setConfigDeviceId(d.id)
            setConfigDeviceName(d.name)
          }}
        >
          Config
        </button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Devices"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button onClick={() => navigate('/devices/new')} style={btnPrimary}>
              Add Device
            </button>
          </>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '300px' }}
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(d) => navigate(`/devices/${d.id}`)}
            emptyMessage="No devices found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}
      <InstrumentConfigEditModal
        isOpen={configDeviceId !== null}
        onClose={() => setConfigDeviceId(null)}
        deviceId={configDeviceId ?? ''}
        deviceName={configDeviceName}
      />
    </div>
  )
}
